const api_host = `/v1`;

export { api_host };

export default {
  // user
  logout: `${api_host}/user/logout`,
  sso_config: `${api_host}/user/sso/config`,
  sso_callback: `${api_host}/user/sso/callback`,
  sso_refresh: `${api_host}/user/sso/refresh`,
  setting: `${api_host}/user/setting`,
  user_info: `${api_host}/user/info`,
  // user deletion / asset recovery
  deletion_event_list: `${api_host}/user_deletion/list`,
  deletion_event_detail: (eventId: string) =>
    `${api_host}/user_deletion/${eventId}`,
  deletion_event_resolve: (eventId: string) =>
    `${api_host}/user_deletion/${eventId}/resolve`,
  deletion_super_admins: `${api_host}/user_deletion/super_admins`,
  tenant_info: `${api_host}/user/tenant_info`,
  set_tenant_info: `${api_host}/user/set_tenant_info`,

  // feature flags
  feature_flags: `${api_host}/feature/flags`,

  // team
  addTenantUser: (tenantId: string) => `${api_host}/tenant/${tenantId}/user`,
  listTenantUser: (tenantId: string) =>
    `${api_host}/tenant/${tenantId}/user/list`,
  deleteTenantUser: (tenantId: string, userId: string) =>
    `${api_host}/tenant/${tenantId}/user/${userId}`,
  updateUserRole: (tenantId: string, userId: string) =>
    `${api_host}/tenant/${tenantId}/user/${userId}/role`,
  listTenant: `${api_host}/tenant/list`,
  agreeTenant: (id: string) => `${api_host}/tenant/agree/${id}`,
  createTeam: `${api_host}/tenant/create`,
  deleteTenant: (id: string) => `${api_host}/tenant/${id}`,
  // llm model
  factories_list: `${api_host}/llm/factories`,
  llm_list: `${api_host}/llm/list`,
  my_llm: `${api_host}/llm/my_llms`,
  set_api_key: `${api_host}/llm/set_api_key`,
  add_llm: `${api_host}/llm/add_llm`,
  add_llm_batch: `${api_host}/llm/add_llm_batch`,
  bedrock_list_models: `${api_host}/llm/bedrock/list_models`,
  delete_llm: `${api_host}/llm/delete_llm`,
  deleteFactory: `${api_host}/llm/delete_factory`,
  share_llm: `${api_host}/llm/share_llm`,
  update_llm_pricing: `${api_host}/llm/update_llm_pricing`,
  shared_llm_list: `${api_host}/llm/shared_llm_list`,

  // knowledge base
  kb_list: `${api_host}/kb/list`,
  create_kb: `${api_host}/kb/create`,
  update_kb: `${api_host}/kb/update`,
  rm_kb: `${api_host}/kb/rm`,
  get_kb_detail: `${api_host}/kb/detail`,
  generate_kb_description: `${api_host}/kb/generate_description`,
  getKnowledgeGraph: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/knowledge_graph`,
  getIngestionSummary: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/ingestion_summary`,
  retryIngestion: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/retry_ingestion`,
  // Which ERP revision this KB's code graph holds (graph_provenance) plus the versions its run
  // history shows. One extra Postgres connection per call on the server — fetch it per KB view,
  // never inside a list loop.
  getKbCodeVersion: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/code_version`,
  // Which recipe at which revision built this KB's code, and what changed upstream since. GET and
  // POST share one path (null / the release, create-or-update); /check reads the repository and can
  // take 30 s; /update starts a whole-release re-ingest.
  getKbCodeRelease: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/code_release`,
  saveKbCodeRelease: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/code_release`,
  checkKbCodeRelease: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/code_release/check`,
  updateKbCodeRelease: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/code_release/update`,
  // Code versions page: every loaded ERP code version, its load jobs and the YAB catalog
  // (api/apps/code_versions_app.py). A folder is a path such as `adaptive/1.0`: its segments are
  // encoded one by one and the slash stays.
  codeVersions: `${api_host}/code_versions`,
  codeVersionsCatalog: `${api_host}/code_versions/catalog`,
  codeVersionsFolder: (
    folder: string,
    query: { rev?: string; base?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (query.rev) q.set('rev', query.rev);
    if (query.base) q.set('base', query.base);
    const qs = q.toString();
    const path = folder.split('/').map(encodeURIComponent).join('/');
    return `${api_host}/code_versions/catalog/${path}${qs ? `?${qs}` : ''}`;
  },
  codeVersionsJobs: `${api_host}/code_versions/jobs`,
  codeVersionsJob: (jobId: string) =>
    `${api_host}/code_versions/jobs/${encodeURIComponent(jobId)}`,
  codeVersionsJobAction: (jobId: string, action: string) =>
    `${api_host}/code_versions/jobs/${encodeURIComponent(jobId)}/${action}`,

  // Client versions code library (api/apps/code_library_app.py).
  codeLibrary: `${api_host}/code_library`,
  codeLibraryRunStop: `${api_host}/code_library/run/stop`,
  codeLibrarySettings: `${api_host}/code_library/settings`,
  codeLibraryClient: (number: string) =>
    `${api_host}/code_library/client/${encodeURIComponent(number)}`,
  codeLibraryClientEdit: (number: string) =>
    `${api_host}/code_library/client/${encodeURIComponent(number)}/edit`,
  codeLibraryUploads: `${api_host}/code_library/uploads`,
  codeLibraryDraftAction: (id: string, action: 'save' | 'discard' | 'cancel') =>
    `${api_host}/code_library/drafts/${encodeURIComponent(id)}/${action}`,
  codeLibraryActiveDraft: `${api_host}/code_library/drafts/active`,
  codeLibrarySheet: `${api_host}/code_library/sheet`,
  codeLibraryVersions: `${api_host}/code_library/versions`,
  codeLibraryVersionAction: (action: string) =>
    `${api_host}/code_library/versions/${action}`,
  codeLibraryHistory: `${api_host}/code_library/history`,
  codeLibraryChangeSet: (id: string) =>
    `${api_host}/code_library/history/${encodeURIComponent(id)}`,
  codeLibrarySheetSource: `${api_host}/code_library/sheet_source`,
  codeLibrarySheetSourceControl: `${api_host}/code_library/sheet_source/control`,
  codeLibrarySheetSourceProbe: `${api_host}/code_library/sheet_source/probe`,
  codeLibraryGroups: `${api_host}/code_library/groups`,
  codeLibraryGroupGap: `${api_host}/code_library/groups/gap`,
  codeLibrarySavedGroups: `${api_host}/code_library/groups/saved`,
  codeLibrarySavedGroup: (id: string) =>
    `${api_host}/code_library/groups/saved/${encodeURIComponent(id)}`,
  codeLibraryOverview: `${api_host}/code_library/overview`,
  codeLibraryAnalytics: `${api_host}/code_library/analytics`,

  // tags
  listTag: (knowledgeId: string) => `${api_host}/kb/${knowledgeId}/tags`,
  listTagByKnowledgeIds: `${api_host}/kb/tags`,
  removeTag: (knowledgeId: string) => `${api_host}/kb/${knowledgeId}/rm_tags`,
  renameTag: (knowledgeId: string) =>
    `${api_host}/kb/${knowledgeId}/rename_tag`,

  // chunk
  chunk_list: `${api_host}/chunk/list`,
  create_chunk: `${api_host}/chunk/create`,
  set_chunk: `${api_host}/chunk/set`,
  get_chunk: `${api_host}/chunk/get`,
  switch_chunk: `${api_host}/chunk/switch`,
  switch_frequently_used: `${api_host}/chunk/switch_frequently_used`,
  rm_chunk: `${api_host}/chunk/rm`,
  retrieval_test: `${api_host}/chunk/retrieval_test`,
  knowledge_graph: `${api_host}/chunk/knowledge_graph`,

  // document
  get_document_list: `${api_host}/document/list`,
  get_document_tree: `${api_host}/document/tree`,
  // A document's latest parse runs, step by step with timings (api/apps/document_app.py:timeline).
  getDocumentTimeline: (documentId: string) =>
    `${api_host}/document/${documentId}/timeline`,
  document_ids: `${api_host}/document/ids`,
  document_rm_by_path: `${api_host}/document/rm_by_path`,
  document_change_status: `${api_host}/document/change_status`,
  document_rm: `${api_host}/document/rm`,
  document_delete: `${api_host}/api/document`,
  document_rename: `${api_host}/document/rename`,
  document_create: `${api_host}/document/create`,
  document_run: `${api_host}/document/run`,
  document_resume: `${api_host}/document/resume`,
  document_restart_step: `${api_host}/document/restart_step`,
  document_change_parser: `${api_host}/document/change_parser`,
  document_thumbnails: `${api_host}/document/thumbnails`,
  get_document_file: `${api_host}/document/get`,
  document_upload: `${api_host}/document/upload`,
  web_crawl: `${api_host}/document/web_crawl`,
  document_infos: `${api_host}/document/infos`,
  upload_and_parse: `${api_host}/document/upload_and_parse`,
  parse: `${api_host}/document/parse`,
  setMeta: `${api_host}/document/set_meta`,

  // chat
  setDialog: `${api_host}/dialog/set`,
  getDialog: `${api_host}/dialog/get`,
  removeDialog: `${api_host}/dialog/rm`,
  listDialog: `${api_host}/dialog/list`,
  setConversation: `${api_host}/conversation/set`,
  getConversation: `${api_host}/conversation/get`,
  getConversationSSE: `${api_host}/conversation/getsse`,
  listConversation: `${api_host}/conversation/list`,
  removeConversation: `${api_host}/conversation/rm`,
  completeConversation: `${api_host}/conversation/completion`,
  deleteMessage: `${api_host}/conversation/delete_msg`,
  thumbup: `${api_host}/conversation/thumbup`,
  tts: `${api_host}/conversation/tts`,
  ask: `${api_host}/conversation/ask`,
  mindmap: `${api_host}/conversation/mindmap`,
  getRelatedQuestions: `${api_host}/conversation/related_questions`,
  // chat for external
  createToken: `${api_host}/api/new_token`,
  listToken: `${api_host}/api/token_list`,
  removeToken: `${api_host}/api/rm`,
  getStats: `${api_host}/api/stats`,
  createExternalConversation: `${api_host}/api/new_conversation`,
  getExternalConversation: `${api_host}/api/conversation`,
  completeExternalConversation: `${api_host}/api/completion`,
  uploadAndParseExternal: `${api_host}/api/document/upload_and_parse`,

  // file manager
  listFile: `${api_host}/file/list`,
  uploadFile: `${api_host}/file/upload`,
  removeFile: `${api_host}/file/rm`,
  renameFile: `${api_host}/file/rename`,
  getAllParentFolder: `${api_host}/file/all_parent_folder`,
  createFolder: `${api_host}/file/create`,
  connectFileToKnowledge: `${api_host}/file2document/convert`,
  getFile: `${api_host}/file/get`,
  moveFile: `${api_host}/file/mv`,

  // system
  getSystemVersion: `${api_host}/system/version`,
  getSystemStatus: `${api_host}/system/status`,
  getSystemTokenList: `${api_host}/system/token_list`,
  createSystemToken: `${api_host}/system/new_token`,
  listSystemToken: `${api_host}/system/token_list`,
  removeSystemToken: `${api_host}/system/token`,
  getSystemConfig: `${api_host}/system/config`,
  setLangfuseConfig: `${api_host}/langfuse/api_key`,

  // flow
  listTemplates: `${api_host}/canvas/templates`,
  listCanvas: `${api_host}/canvas/list`,
  listCanvasTeam: `${api_host}/canvas/listteam`,
  getCanvas: `${api_host}/canvas/get`,
  getCanvasSSE: `${api_host}/canvas/getsse`,
  removeCanvas: `${api_host}/canvas/rm`,
  setCanvas: `${api_host}/canvas/set`,
  settingCanvas: `${api_host}/canvas/setting`,
  generateCanvasDescription: `${api_host}/canvas/generate_description`,
  generateMasterAgentPrompts: `${api_host}/canvas/generate_master_agent_prompts`,
  getListVersion: `${api_host}/canvas/getlistversion`,
  getVersion: `${api_host}/canvas/getversion`,
  resetCanvas: `${api_host}/canvas/reset`,
  runCanvas: `${api_host}/canvas/completion`,
  runCanvasEvents: (runId: string) => `${api_host}/canvas/run/${runId}/events`,
  runCanvasCancel: (runId: string) => `${api_host}/canvas/run/${runId}/cancel`,
  testDbConnect: `${api_host}/canvas/test_db_connect`,
  getInputElements: `${api_host}/canvas/input_elements`,
  debug: `${api_host}/canvas/debug`,
  consentResponse: `${api_host}/canvas/consent`,
  consentPending: `${api_host}/canvas/consent/pending`,

  // connectors
  connectorProviders: `${api_host}/connector/providers`,
  connectorAuthUrl: (provider: string) =>
    `${api_host}/connector/${provider}/auth_url`,
  // database connections (dedicated Connectors page)
  dbConnections: `${api_host}/database_connector/connections`,
  dbConnection: (id: string) =>
    `${api_host}/database_connector/connections/${id}`,
  dbConnectionTest: `${api_host}/database_connector/test`,
  connectorAccounts: (provider: string) =>
    `${api_host}/connector/${provider}/accounts`,
  connectorDisconnectAccount: (provider: string, accountId: string) =>
    `${api_host}/connector/${provider}/accounts/${accountId}`,
  connectorAccountConfig: (provider: string, accountId: string) =>
    `${api_host}/connector/${provider}/accounts/${accountId}/config`,
  connectorFolders: (provider: string) =>
    `${api_host}/connector/${provider}/folders`,
  connectorImportFoldersToKb: (provider: string) =>
    `${api_host}/connector/${provider}/import_folders_to_kb`,
  // YAB release catalog (svn code ingest): list/upsert, per-name delete, recipe test, ingest
  connectorYabReleases: (provider: string) =>
    `${api_host}/connector/${provider}/yab_releases`,
  connectorYabRelease: (provider: string, name: string) =>
    `${api_host}/connector/${provider}/yab_releases/${encodeURIComponent(name)}`,
  connectorYabReleaseTest: (provider: string) =>
    `${api_host}/connector/${provider}/yab_releases/test`,
  connectorYabReleaseBrowse: (provider: string) =>
    `${api_host}/connector/${provider}/yab_releases/browse`,
  connectorYabReleaseLog: (provider: string) =>
    `${api_host}/connector/${provider}/yab_releases/log`,
  connectorYabResolveVersion: (provider: string) =>
    `${api_host}/connector/${provider}/yab_releases/resolve_version`,
  connectorIngestRecipe: (provider: string) =>
    `${api_host}/connector/${provider}/ingest_recipe`,
  connectorAddSource: (provider: string) =>
    `${api_host}/connector/${provider}/add_source`,
  // Argo-first windowed import (atlassian_onprem only, CONNECTOR_INGEST_ORCHESTRATOR=argo gated).
  connectorImportWindowed: (provider: string) =>
    `${api_host}/connector/${provider}/import_windowed`,
  connectorImportProgress: (
    provider: string,
    kbId: string,
    accountEmail: string,
  ) =>
    `${api_host}/connector/${provider}/import_progress?kb_id=${kbId}&account_email=${encodeURIComponent(accountEmail)}`,
  connectorCancelImport: (provider: string) =>
    `${api_host}/connector/${provider}/cancel_import`,
  // The Auto-sync page: every provider's auto-sync for one knowledge base (connector_app.py).
  autoSyncKbs: `${api_host}/connector/auto_sync/kbs`,
  autoSyncOverview: (kbId: string) =>
    `${api_host}/connector/auto_sync/overview?kb_id=${kbId}`,
  autoSyncControl: `${api_host}/connector/auto_sync/control`,
  // Connector run ledger (Task 10): durable run history + retry/preflight actions
  connectorRuns: (
    provider: string,
    kbId: string,
    limit = 20,
    offset = 0,
    filter: { kind?: string; status?: string; since?: string; q?: string } = {},
  ) =>
    `${api_host}/connector/${provider}/runs?kb_id=${kbId}&limit=${limit}&offset=${offset}${(
      ['kind', 'status', 'since', 'q'] as const
    )
      .filter((key) => filter[key])
      .map((key) => `&${key}=${encodeURIComponent(filter[key] as string)}`)
      .join('')}`,
  connectorRunDetail: (provider: string, runId: string) =>
    `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}`,
  // Live run progress, computed from Postgres (never from Argo). `argo=1` adds the workflow's
  // cached phase; `refresh=1` alongside it bypasses that cache — that pair is the Refresh button.
  connectorRunProgress: (
    provider: string,
    runId: string,
    opts: { argo?: boolean; refresh?: boolean } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.argo) q.set('argo', '1');
    if (opts.refresh) q.set('refresh', '1');
    const qs = q.toString();
    return `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}/progress${qs ? `?${qs}` : ''}`;
  },
  // One import's documents and their outcomes, a page at a time (the History tab's "See details").
  connectorRunDocuments: (
    provider: string,
    runId: string,
    query: {
      outcome?: string;
      step?: string;
      reason?: string;
      q?: string;
      after?: string;
      limit?: number;
      counts?: boolean;
      skipped_limit?: number;
    },
  ) => {
    const q = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') q.set(key, String(value));
    });
    const qs = q.toString();
    return `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}/documents${qs ? `?${qs}` : ''}`;
  },
  // Where one import's time went: document work, pods starting, bookkeeping, waits between pods.
  connectorRunTiming: (provider: string, runId: string) =>
    `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}/timing`,
  connectorRunWorkflow: (provider: string, runId: string, refresh = false) =>
    `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}/workflow${refresh ? '?refresh=1' : ''}`,
  connectorRunWorkflowAction: (
    provider: string,
    runId: string,
    action: string,
  ) =>
    `${api_host}/connector/${provider}/runs/${encodeURIComponent(runId)}/workflow/${encodeURIComponent(action)}`,
  connectorRetryFailed: (provider: string) =>
    `${api_host}/connector/${provider}/retry_failed`,
  // Re-ingest a KB from its durable staged embeddings (no re-embed, no SVN) — object-store-staged
  // pipeline. A model/dim mismatch (R1 gate) is rejected synchronously with a legible message.
  connectorReingest: (provider: string) =>
    `${api_host}/connector/${provider}/reingest`,
  connectorPreflight: (provider: string) =>
    `${api_host}/connector/${provider}/preflight`,
  connectorSyncedFolders: (provider: string, kbId: string) =>
    `${api_host}/connector/${provider}/synced_folders?kb_id=${kbId}`,
  connectorFolderFiles: (
    provider: string,
    folderId: string,
    accountEmail: string,
  ) =>
    `${api_host}/connector/${provider}/folders/${encodeURIComponent(folderId)}/files?account_email=${encodeURIComponent(accountEmail)}`,
  connectorEnumerateFolder: (provider: string, folderId: string) =>
    `${api_host}/connector/${provider}/folders/${encodeURIComponent(folderId)}/enumerate`,
  connectorEnumerateProgress: (provider: string, jobId: string) =>
    `${api_host}/connector/${provider}/enumerate_progress?job_id=${encodeURIComponent(jobId)}`,
  connectorRemoveSyncedFolder: (
    provider: string,
    folderId: string,
    kbId: string,
    accountEmail: string,
  ) =>
    `${api_host}/connector/${provider}/synced_folders/${folderId}?kb_id=${kbId}&account_email=${encodeURIComponent(accountEmail)}`,
  // Query-param variant for folder ids that are raw URLs (SVN module URLs): a percent-encoded
  // slash in the PATH gets mangled by common ingress defaults (nginx merge_slashes, Apache
  // AllowEncodedSlashes) before Flask sees it; query strings pass through proxies untouched.
  connectorRemoveSyncedFolderByQuery: (
    provider: string,
    folderId: string,
    kbId: string,
    accountEmail: string,
  ) =>
    `${api_host}/connector/${provider}/synced_folders?kb_id=${kbId}&account_email=${encodeURIComponent(accountEmail)}&folder_id=${encodeURIComponent(folderId)}`,
  connectorRemoveSyncedFile: (
    provider: string,
    fileId: string,
    kbId: string,
    accountEmail: string,
  ) =>
    `${api_host}/connector/${provider}/synced_files/${encodeURIComponent(fileId)}?kb_id=${kbId}&account_email=${encodeURIComponent(accountEmail)}`,
  gcsConnectServiceAccount: `${api_host}/connector/gcs/connect_service_account`,
  connectorGcsProjects: (accountEmail: string) =>
    `${api_host}/connector/gcs/projects?account_email=${encodeURIComponent(accountEmail)}`,
  connectorConnectCredentials: (provider: string) =>
    `${api_host}/connector/${provider}/connect_credentials`,
  connectorConnectServiceAccount: (provider: string) =>
    `${api_host}/connector/${provider}/connect_service_account`,
  // overview / dashboard
  overviewStats: `${api_host}/overview/stats`,
  connectorWebhookRegister: (provider: string) =>
    `${api_host}/connector/${provider}/webhook/register`,
  connectorAutoSync: (provider: string) =>
    `${api_host}/connector/${provider}/auto_sync`,
  connectorWebhooks: (provider: string, kbId: string) =>
    `${api_host}/connector/${provider}/webhooks?kb_id=${kbId}`,
  // Background attachments of a connector's tickets/pages; `path` is '', '/summary', '/rollup', ...
  connectorAttachments: (provider: string, path = '') =>
    `${api_host}/connector/${provider}/attachments${path}`,
};
